import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons/faArrowRight';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { Text, TextType } from 'ui-components/components/text';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { BannerStatus, BannerLayout } from 'ui-components/components/banner-message/banner-enums';

import type { LtlQuote } from '@shipstation/client-common/types/ltl';
import { useFreightQuote } from '../../freight-quote-context';
import { QuoteCard } from '../booking-wizard/components/quote-card/quote-card';
import { QuoteDetailsModal } from '../booking-wizard/components/quote-details-modal/quote-details-modal';
import { freightQuoteResultsMessages } from './messages';

import style from './freight-quote-results.module.less';

export const FreightQuoteResults = () => {
  const { state: wizardState, localDispatch, saveQuote } = useFreightQuote();

  const quotes = wizardState.quotes;
  const selectedQuote = wizardState.selectedQuote;
  const isLoadingQuotes = wizardState.isLoadingQuotes;
  const isSavingQuote = wizardState.isSavingQuote;
  const hasQuoteError = wizardState.hasQuoteError;
  const quoteErrorMessage = wizardState.quoteErrorMessage;
  const hasBookingError = wizardState.hasBookingError;
  const bookingErrorMessage = wizardState.bookingErrorMessage;
  const savedQuoteFreightShipmentId = wizardState.savedQuoteFreightShipmentId;

  const isSavedQuoteReopen = Boolean(savedQuoteFreightShipmentId);

  const [sortBy, setSortBy] = useState<'cheapest' | 'fastest'>('cheapest');
  const [detailsQuote, setDetailsQuote] = useState<LtlQuote | null>(null);

  const handleSelectQuote = useCallback(
    (quote: LtlQuote) => {
      localDispatch({ type: 'SELECT_QUOTE', quote });
    },
    [localDispatch],
  );

  const handleCloseDetails = useCallback(() => {
    setDetailsQuote(null);
  }, []);

  const handleSaveQuoteClick = useCallback(() => {
    saveQuote();
  }, [saveQuote]);

  const handleBookShipmentClick = useCallback(() => {
    localDispatch({ type: 'SET_DRAWER_VIEW', view: 'booking-wizard' });
  }, [localDispatch]);

  const quotesWithCarrierCount = useMemo(() => {
    if (!quotes) return { uniqueCarrierCount: 0, totalCount: 0 };
    const uniqueCarriers = new Set(quotes.map((q) => q.carrierCode));
    return { uniqueCarrierCount: uniqueCarriers.size, totalCount: quotes.length };
  }, [quotes]);

  const sortedQuotes = useMemo(() => {
    if (!quotes) return [];
    return [...quotes].sort((a, b) => {
      if (sortBy === 'cheapest') {
        return a.totalCharges - b.totalCharges;
      }
      const transitDiff = a.transitDays - b.transitDays;
      if (transitDiff !== 0) return transitDiff;
      return a.totalCharges - b.totalCharges;
    });
  }, [quotes, sortBy]);

  const handleSortCheapest = useCallback(() => setSortBy('cheapest'), []);
  const handleSortFastest = useCallback(() => setSortBy('fastest'), []);

  const hasQuotes = quotes?.length > 0;
  const isQuotesExpired = isSavedQuoteReopen && !isLoadingQuotes && !hasQuotes;
  const isEmptyState = !isLoadingQuotes && !hasQuoteError && !hasBookingError && !hasQuotes && !isQuotesExpired;

  return (
    <div className={style['freight-quote-results']}>
      <div className={style['freight-quote-results-header']}>
        <FlexRow className={style['header-title-row']} gap={Gap.Space16}>
          <Text textType={TextType.H2} value={freightQuoteResultsMessages.headerTitle} />
          {hasQuotes && (
            <Text
              formattedMessageValues={{
                count: quotesWithCarrierCount.uniqueCarrierCount,
                total: quotesWithCarrierCount.totalCount,
              }}
              textType={TextType.Caption}
              value={freightQuoteResultsMessages.carrierCount}
            />
          )}
        </FlexRow>
        {hasQuotes && (
          <div className={style['sort-controls']}>
            <Text className={style['sort-label']} textType={TextType.Caption} value={freightQuoteResultsMessages.sortByLabel} />
            <Button
              className={classnames(style['sort-pill'], { [style['sort-pill-active']]: sortBy === 'cheapest' })}
              onClick={handleSortCheapest}
              theme={THEME_CONSTANTS.unstyled}
            >
              <FormattedMessage {...freightQuoteResultsMessages.sortCheapest} />
            </Button>
            <Button
              className={classnames(style['sort-pill'], { [style['sort-pill-active']]: sortBy === 'fastest' })}
              onClick={handleSortFastest}
              theme={THEME_CONSTANTS.unstyled}
            >
              <FormattedMessage {...freightQuoteResultsMessages.sortFastest} />
            </Button>
          </div>
        )}
      </div>

      <div className={style['freight-quote-results-body']}>
        {hasBookingError && (
          <BannerMessageV2
            content={<Text textType={TextType.Paragraph} value={bookingErrorMessage} />}
            layout={BannerLayout.Inline}
            status={BannerStatus.Error}
          />
        )}

        {hasQuoteError && (
          <BannerMessageV2
            content={<Text textType={TextType.Paragraph} value={quoteErrorMessage} />}
            layout={BannerLayout.Inline}
            status={BannerStatus.Error}
          />
        )}

        {isQuotesExpired && (
          <BannerMessageV2
            content={<Text textType={TextType.Paragraph} value={freightQuoteResultsMessages.quotesExpired} />}
            layout={BannerLayout.Inline}
            status={BannerStatus.Warning}
          />
        )}

        {isLoadingQuotes && (
          <FlexColumn className={style['loading-state']} gap={Gap.Space16}>
            <LoadingIndicator className={style['loading-spinner']} />
          </FlexColumn>
        )}

        {isEmptyState && (
          <FlexColumn className={style['empty-state']}>
            <Text textType={TextType.Paragraph} value={freightQuoteResultsMessages.emptyState} />
          </FlexColumn>
        )}

        {hasQuotes && (
          <>
            {sortedQuotes.map((quote: LtlQuote) => {
              const isSelected = selectedQuote?.offerId === quote.offerId;

              return (
                <QuoteCard
                  key={quote.offerId}
                  isSelected={isSelected}
                  onSelect={handleSelectQuote}
                  onViewDetails={setDetailsQuote}
                  quote={quote}
                />
              );
            })}
          </>
        )}
      </div>

      <div className={style['freight-quote-results-footer']}>
        <Button disabled={!selectedQuote} isLoading={isSavingQuote} onClick={handleSaveQuoteClick} theme={THEME_CONSTANTS.tertiary}>
          <FormattedMessage {...(isSavedQuoteReopen ? freightQuoteResultsMessages.updateQuote : freightQuoteResultsMessages.saveQuote)} />
        </Button>
        <Button
          className={style['book-shipment-button']}
          disabled={!selectedQuote}
          onClick={handleBookShipmentClick}
          theme={THEME_CONSTANTS.primary}
        >
          <FormattedMessage {...freightQuoteResultsMessages.bookShipment} />
          <FontAwesomeIcon icon={faArrowRight} />
        </Button>
      </div>
      {detailsQuote && <QuoteDetailsModal onClose={handleCloseDetails} quote={detailsQuote} />}
    </div>
  );
};
